// src/services/comments.service.js
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from "firebase/firestore";
import { db } from "./firebase/firebaseconfig";

class CommentsService {
  constructor() {
    this.collectionName = "comments";
  }

  // Get all comments
  async getAllComments() {
    try {
      const commentsRef = collection(db, this.collectionName);
      const q = query(commentsRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      
      const comments = [];
      querySnapshot.forEach((doc) => {
        comments.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return {
        success: true,
        comments: comments
      };
    } catch (error) {
      console.error("Get all comments error:", error);
      return {
        success: false,
        error: error.message,
        comments: []
      };
    }
  }

  // Get comments by product ID
  async getCommentsByProduct(productId) {
    try {
      const commentsRef = collection(db, this.collectionName);
      const q = query(
        commentsRef, 
        where("productId", "==", productId),
        where("status", "==", "approved"),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      
      const comments = [];
      querySnapshot.forEach((doc) => {
        comments.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return {
        success: true,
        comments: comments
      };
    } catch (error) {
      console.error("Get comments by product error:", error);
      return {
        success: false,
        error: error.message,
        comments: []
      };
    }
  }

  // Get approved comments for display
  async getApprovedComments(limitCount = 10) {
    try {
      const commentsRef = collection(db, this.collectionName);
      const q = query(
        commentsRef, 
        where("status", "==", "approved"),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      
      const comments = [];
      querySnapshot.forEach((doc) => {
        comments.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return {
        success: true,
        comments: comments
      };
    } catch (error) {
      console.error("Get approved comments error:", error);
      return {
        success: false,
        error: error.message,
        comments: []
      };
    }
  }

  // Add new comment
  async addComment(commentData) {
    try {
      const commentsRef = collection(db, this.collectionName);
      
      // Generate avatar from name
      const nameParts = commentData.author.split(' ');
      const avatar = nameParts.length > 1 
        ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
        : commentData.author.substring(0, 2).toUpperCase();

      const docRef = await addDoc(commentsRef, {
        ...commentData,
        avatar: avatar,
        status: 'pending', // New comments start as pending
        createdAt: serverTimestamp()
      });

      return {
        success: true,
        commentId: docRef.id,
        message: "Commentaire ajouté avec succès"
      };
    } catch (error) {
      console.error("Add comment error:", error);
      return {
        success: false,
        error: error.message,
        message: "Échec de l'ajout du commentaire"
      };
    }
  }

  // Update comment status
  async updateCommentStatus(commentId, status) {
    try {
      const commentRef = doc(db, this.collectionName, commentId);
      await updateDoc(commentRef, {
        status: status,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        message: "Statut du commentaire mis à jour"
      };
    } catch (error) {
      console.error("Update comment status error:", error);
      return {
        success: false,
        error: error.message,
        message: "Échec de la mise à jour du statut"
      };
    }
  }

  // Delete comment
  async deleteComment(commentId) {
    try {
      const commentRef = doc(db, this.collectionName, commentId);
      await deleteDoc(commentRef);

      return {
        success: true,
        message: "Commentaire supprimé avec succès"
      };
    } catch (error) {
      console.error("Delete comment error:", error);
      return {
        success: false,
        error: error.message,
        message: "Échec de la suppression du commentaire"
      };
    }
  }

  // Get comment statistics
  async getCommentStats() {
    try {
      const commentsRef = collection(db, this.collectionName);
      const querySnapshot = await getDocs(commentsRef);
      
      let total = 0;
      let approved = 0;
      let pending = 0;
      let rejected = 0;
      let totalRating = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        total++;
        totalRating += data.rating || 0;
        
        if (data.status === 'approved') approved++;
        else if (data.status === 'pending') pending++;
        else if (data.status === 'rejected') rejected++;
      });

      return {
        success: true,
        stats: {
          total,
          approved,
          pending,
          rejected,
          avgRating: total > 0 ? (totalRating / total).toFixed(1) : 0
        }
      };
    } catch (error) {
      console.error("Get comment stats error:", error);
      return {
        success: false,
        error: error.message,
        stats: {
          total: 0,
          approved: 0,
          pending: 0,
          rejected: 0,
          avgRating: 0
        }
      };
    }
  }
}

export default new CommentsService();